import React from "react";
import { Link } from "react-router-dom";
import HeroIcon from "../common/HeroIcon";
import { formatDate } from "../../utils/date";

const PATTERN_TYPE_ICONS = {
  type: "document-text",
  main_topic: "tag",
  content_angle: "light-bulb",
  format: "squares-2x2",
  structure: "queue-list",
  categories: "folder",
  tone: "swatch",
  audience: "users",
  language: "language",
  hook_type: "bolt",
  hook_phrase: "chat-bubble-left-ellipsis",
  hook_visual: "eye",
  cta_type: "cursor-arrow-rays",
  cta_style: "megaphone",
  hashtags: "hashtag",
  channel_tags: "at-symbol",
  text_hook: "bars-3-bottom-left",
  song: "musical-note",
  artist: "user-circle",
};
const getPatternIcon = (t) => PATTERN_TYPE_ICONS[t] || "squares-2x2";

const AudienceIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-theme-text opacity-70 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const tagClassName =
  "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs border border-theme-secondary bg-theme-secondary text-theme-text opacity-80 hover:opacity-100 hover:bg-theme-main";

const sceneFrameClassName =
  "relative w-[7.5rem] sm:w-36 flex-shrink-0 aspect-[9/16] rounded-lg overflow-hidden border border-theme-secondary bg-theme-secondary bg-opacity-50";

const topCaptionStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(2px)",
  color: "#c4ff4d",
};

const topCaptionClassName =
  "inline-block max-w-full line-clamp-2 rounded px-1.5 py-0.5 text-center text-[9px] font-bold uppercase leading-tight tracking-wide sm:text-[10px]";

const ScriptCard = ({ script, onOpen }) => {
  const title = script?.idea_title || "Untitled script";
  const description = (script?.script_data?.description || "").trim();
  const audiences = Array.isArray(script?.audiences) ? script.audiences : [];
  const patterns = Array.isArray(script?.patterns) ? script.patterns : [];
  const scenes = Array.isArray(script?.script_data?.scenes)
    ? script.script_data.scenes
    : [];
  const hasTags = audiences.length > 0 || patterns.length > 0;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(script.hash)}
      className="w-full rounded-lg border border-theme-secondary bg-theme-main p-4 text-left transition-colors hover:bg-theme-secondary focus:outline-none focus:ring-2 focus:ring-theme-primary"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="min-w-0 truncate text-base font-semibold text-theme-text">
              {title}
            </h3>
            {hasTags ? (
              <div
                className="flex flex-wrap items-center gap-1.5"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {audiences.map((audience) => {
                  const label = audience?.name || "Unnamed audience";
                  return audience?.hash ? (
                    <Link
                      key={audience.hash}
                      to={`/audience/${audience.hash}/`}
                      className={tagClassName}
                    >
                      <AudienceIcon />
                      <span className="truncate max-w-[180px]">{label}</span>
                    </Link>
                  ) : (
                    <span key={label} className={tagClassName}>
                      <AudienceIcon />
                      <span className="truncate max-w-[180px]">{label}</span>
                    </span>
                  );
                })}
                {patterns.map((pattern) => {
                  const url =
                    pattern?.audience_hash && pattern?.pattern_type && pattern?.pattern_key
                      ? `/audience/${pattern.audience_hash}/patterns/${encodeURIComponent(pattern.pattern_type)}/${encodeURIComponent(pattern.pattern_key)}/`
                      : null;
                  const label = pattern?.display_label || "Pattern";
                  const icon = (
                    <HeroIcon
                      name={getPatternIcon(pattern?.pattern_type)}
                      className="w-3.5 h-3.5 text-theme-text opacity-70 flex-shrink-0"
                    />
                  );
                  return url ? (
                    <Link
                      key={`${pattern.audience_hash}-${pattern.pattern_type}-${pattern.pattern_key}`}
                      to={url}
                      className={tagClassName}
                    >
                      {icon}
                      <span className="truncate max-w-[180px]">{label}</span>
                    </Link>
                  ) : (
                    <span
                      key={`${pattern.audience_hash}-${pattern.pattern_type}-${pattern.pattern_key}`}
                      className={tagClassName}
                    >
                      {icon}
                      <span className="truncate max-w-[180px]">{label}</span>
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
          {description ? (
            <p className="mt-1 line-clamp-2 text-sm text-theme-text opacity-70">{description}</p>
          ) : null}
        </div>
        <span className="flex-shrink-0 text-xs text-theme-text opacity-60">
          {formatDate(script.create_date)}
        </span>
      </div>

      {scenes.length > 0 ? (
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5">
          {scenes.map((scene) => {
            const onScreenCaption = scene?.on_screen_text?.trim() || null;
            return (
              <div
                key={scene.scene_number ?? scene.image_hash ?? scene.visual_description}
                className={sceneFrameClassName}
              >
                {scene.image_url ? (
                  <img
                    src={scene.image_url}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : null}
                {onScreenCaption ? (
                  <div className="absolute inset-x-0 top-0 z-10 px-1 pt-1">
                    <div className="flex justify-center">
                      <p className={topCaptionClassName} style={topCaptionStyle}>
                        {onScreenCaption}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </button>
  );
};

export default ScriptCard;
